(function (root, factory) {
  'use strict';

  var api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.KiiveStudioTools = root.KiiveStudioTools || {};
    root.KiiveStudioTools.copy = api;
  }
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  var DEFAULT_FEEDBACK_DURATION = 1400;

  function createCopyManager(options) {
    var settings = options || {};
    var statusNode = settings.statusNode;
    var documentObject = settings.document || (root && root.document);
    var navigatorObject = settings.navigator || (root && root.navigator) || {};
    var setTimer = settings.setTimeout || (root && root.setTimeout);
    var clearTimer = settings.clearTimeout || (root && root.clearTimeout);
    var feedbackDuration = settings.feedbackDuration === undefined
      ? DEFAULT_FEEDBACK_DURATION
      : settings.feedbackDuration;
    var feedbackTimer = null;
    var activeFeedbackButton = null;
    var requestToken = 0;
    var destroyed = false;

    if (!statusNode || !documentObject || typeof setTimer !== 'function' || typeof clearTimer !== 'function') {
      throw new TypeError('Copy manager requires a status node and browser document and timer APIs.');
    }

    if (!Number.isFinite(feedbackDuration) || feedbackDuration < 0) {
      throw new RangeError('Feedback duration must be a non-negative finite number.');
    }

    function clearFeedback() {
      clearTimer(feedbackTimer);
      feedbackTimer = null;

      if (activeFeedbackButton) {
        activeFeedbackButton.dataset.feedback = '';
      }

      activeFeedbackButton = null;
      statusNode.textContent = '';
    }

    function invalidate() {
      requestToken += 1;
      clearFeedback();
    }

    function isCurrent(token) {
      return !destroyed && token === requestToken;
    }

    async function writeToClipboard(value, token) {
      if (navigatorObject.clipboard && typeof navigatorObject.clipboard.writeText === 'function') {
        try {
          await navigatorObject.clipboard.writeText(value);
          return true;
        } catch (error) {
          // Continue to the browser fallback when Clipboard API access is unavailable.
        }
      }

      if (!isCurrent(token)) {
        return false;
      }

      var activeElement = documentObject.activeElement;
      var temporaryInput = documentObject.createElement('textarea');
      temporaryInput.value = value;
      temporaryInput.setAttribute('readonly', '');
      temporaryInput.style.position = 'fixed';
      temporaryInput.style.opacity = '0';
      var copied = false;

      try {
        documentObject.body.appendChild(temporaryInput);
        temporaryInput.select();
        copied = documentObject.execCommand('copy');
      } finally {
        try {
          temporaryInput.remove();
        } finally {
          if (activeElement && typeof activeElement.focus === 'function') {
            try {
              activeElement.focus({ preventScroll: true });
            } catch (error) {
              activeElement.focus();
            }
          }
        }
      }

      if (!copied) {
        throw new Error('Clipboard copy failed.');
      }

      return true;
    }

    function showFeedback(button, visibleMessage, announcement) {
      clearFeedback();
      button.dataset.feedback = visibleMessage;
      activeFeedbackButton = button;
      statusNode.textContent = announcement;
      feedbackTimer = setTimer(clearFeedback, feedbackDuration);
    }

    async function copy(button, value, messages) {
      if (destroyed) {
        return;
      }

      var copyMessages = messages || {};
      var token = requestToken + 1;
      requestToken = token;
      clearFeedback();

      try {
        var copied = await writeToClipboard(value, token);

        if (!copied || !isCurrent(token)) {
          return;
        }

        showFeedback(
          button,
          copyMessages.successVisible || 'Copied',
          copyMessages.successAnnouncement || 'Copied ' + value + '.'
        );
      } catch (error) {
        if (!isCurrent(token)) {
          return;
        }

        showFeedback(
          button,
          copyMessages.failureVisible || 'Copy failed',
          copyMessages.failureAnnouncement || 'The value could not be copied.'
        );
      }
    }

    function destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;
      invalidate();
    }

    return Object.freeze({
      clearFeedback: clearFeedback,
      copy: copy,
      destroy: destroy,
      invalidate: invalidate
    });
  }

  return Object.freeze({
    DEFAULT_FEEDBACK_DURATION: DEFAULT_FEEDBACK_DURATION,
    createCopyManager: createCopyManager
  });
});
